import { createBody, emptyDraft, type FormField, type NewTicketDraft } from "../../service/tickets/new-ticket-form";
import { NEW_TICKET_TITLE_MAX } from "../../service/tickets/ticket-constants";
import type { TicketCreate, TicketTypeOut } from "../../service/tickets/ticket-types";
import { parseHandoff } from "./handoff-card";
import type { ConversationDetail } from "./inbox-model";

/** The words the handoff stored for the team (the AI summary, or the visitor's own words when the model was down); `null` when there are none. */
export function handoffSummary(detail: Pick<ConversationDetail, "handoff">): string | null {
  return parseHandoff(detail.handoff)?.summary ?? null;
}

/** The ticket type the form starts on: an incident when the workspace has one, else the first type a client may raise. */
export function defaultType(types: readonly TicketTypeOut[]): TicketTypeOut | undefined {
  return types.find((type) => type.system_key === "incident") ?? types[0];
}

/**
 * The form as the inbox opens it: the visitor as a new requester (name, and the e-mail they left when they handed over), the
 * chat's title as the ticket title and the handoff summary as the description. Everything stays editable.
 */
export function convertDraft(type: TicketTypeOut | undefined, detail: Pick<ConversationDetail, "conversation" | "visitor" | "handoff">): NewTicketDraft {
  const { conversation, visitor } = detail;
  const title = (conversation.title ?? conversation.preview ?? "").trim().slice(0, NEW_TICKET_TITLE_MAX);
  return {
    ...emptyDraft(type),
    requesterMode: "new",
    newName: visitor.display_name.trim(),
    newEmail: parseHandoff(detail.handoff)?.contactEmail ?? "",
    title,
    description: handoffSummary(detail) ?? "",
  };
}

/** `POST /v1/tickets` body for a chat converted to a ticket: the console's create body, marked as raised from that conversation. */
export function convertBody(draft: NewTicketDraft, fields: readonly FormField[], conversationId: string): TicketCreate {
  return { ...createBody(draft, fields), origin: "conversation", source_conversation_id: conversationId };
}
